import React, { useState } from 'react';

const FAQs = () => {
    const [openIndex, setOpenIndex] = useState(null);

    const toggleFAQ = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    const faqs = [
        {
            question: "What is SphereSix?",
            answer: "SphereSix is an online carbon modeling platform that empowers farmers to access the carbon credit market. We help small to medium-sized farmers measure their carbon capture potential, estimate earnings, implement sustainable practices, and connect with businesses looking to offset their emissions."
        },
        {
            question: "How does the carbon modeling calculator work?",
            answer: "Our carbon modeling calculator allows farmers to input data about their land (such as acreage, soil type, and current farming practices). The system then analyzes this information to estimate the carbon sequestration potential and projects how many carbon credits could be generated. This gives farmers a clear picture of potential earnings before they commit to any changes."
        },
        {
            question: "What information do I need to use the calculator?",
            answer: "To get started, you'll need basic information about your farm including total acreage, soil types, current farming practices, and which sustainable methods you're interested in implementing. The more details you provide, the more accurate our projections will be, but our system is designed to work even with minimal information."
        },
        {
            question: "Are carbon credits legitimate?",
            answer: "Yes. SphereSix only deals with high-integrity, independently verified carbon credits. All credits sold through our platform are certified to Gold Standard or ISO14064 Part 2 standards, ensuring they represent genuine, measurable, and permanent carbon reductions. These certifications are the 'gold card' for verification, giving businesses confidence in the legitimacy of their carbon offset purchases."
        },
        {
            question: "How much can farmers earn through carbon credits?",
            answer: "Earnings vary based on farm size, soil type, practices implemented, and market conditions. Carbon prices in the UK currently range from £40-£100 per ton and are expected to rise. As demonstrated in our pilot program, farmers can potentially earn significant income - our first farmer is projected to earn upwards of £21,000 over five years through carbon offsetting."
        },
        {
            question: "What sustainable farming practices qualify for carbon credits?",
            answer: "Various practices can qualify, including but not limited to: no-till farming, cover cropping, crop rotation, agroforestry, improved grazing management, and organic farming methods. Our platform helps identify which practices would be most beneficial for your specific land and situation."
        },
        {
            question: "How does the verification process work?",
            answer: "SphereSix facilitates third-party verification through trusted partners like Carbon Check. This ensures accurate measurement and reporting of carbon sequestration. The verification process involves soil sampling, analysis, and documentation to confirm the amount of carbon being captured. All data is recorded securely, increasing transparency and credibility for potential buyers."
        },
        {
            question: "How does SphereSix make money?",
            answer: "SphereSix generates revenue by taking a 25% commission on each carbon credit sale. This business model allows us to provide our platform, educational resources, and support services while ensuring farmers receive the majority of the proceeds from their sustainable practices."
        },
        {
            question: "Who buys the carbon credits?",
            answer: "Carbon credits are purchased by businesses and organizations looking to offset their emissions and meet sustainability goals. Many companies have made net-zero commitments and need carbon credits to achieve these targets. The demand is growing rapidly as more businesses prioritize environmental responsibility, particularly for high-integrity, verifiable credits like those offered through SphereSix."
        },
        {
            question: "How long does it take to generate and sell carbon credits?",
            answer: "The timeline varies depending on the practices implemented and verification requirements. Typically, farmers can begin generating credits within the first growing season after implementing new practices. The verification process usually takes a few months, after which credits can be listed on our marketplace for sale. Our platform provides real-time updates throughout the process."
        },
        {
            question: "Do I need special equipment or expertise to participate?",
            answer: "No special equipment or expertise is required to start. SphereSix provides step-by-step guidance, educational resources, and ongoing support to help farmers implement sustainable practices. Our platform is designed to be user-friendly and accessible regardless of technical expertise."
        },
        {
            question: "What makes SphereSix different from other carbon credit platforms?",
            answer: "SphereSix stands out by focusing specifically on small to medium-sized farms and providing personalized, one-on-one support. Our platform offers transparency, simplicity, and education that larger competitors may overlook. We prioritize building trust in the carbon credit market and ensuring farmers have all the information and guidance they need to succeed."
        },
        {
            question: "Where does SphereSix operate?",
            answer: "Currently, SphereSix operates in the UK, focusing on small to medium-sized farms (under 10,000 acres). We have plans to expand to other countries by 2027, as outlined in our roadmap."
        },
        {
            question: "How do I get started with SphereSix?",
            answer: "Getting started is easy! Sign up on our website to access our carbon modeling calculator. Input your farm details to receive an initial estimate of your carbon capture potential and projected earnings. Our team will follow up to discuss next steps, answer questions, and help you develop a customized sustainability plan for your farm."
        }
    ];

    return (
        <div className="min-h-screen py-16 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                <div className="text-center mb-16">
                    <h1 className="text-4xl sm:text-5xl font-bold mb-6 bg-gradient-to-r from-green-400 to-teal-400 bg-clip-text text-transparent">
                        Frequently Asked Questions
                    </h1>
                    <p className="text-lg text-gray-700 max-w-2xl mx-auto">
                        Everything you need to know about SphereSix, our carbon modeling calculator, and how we're helping farmers profit from sustainability.
                    </p>
                </div>

                <div className="space-y-6">
                    {faqs.map((faq, index) => (
                        <div 
                            key={index} 
                            className="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg"
                        >
                            <button 
                                className="w-full px-6 py-5 text-left focus:outline-none flex justify-between items-center"
                                onClick={() => toggleFAQ(index)}
                            >
                                <span className="text-lg font-bold text-gray-500">{faq.question}</span>
                                <svg 
                                    className={`w-5 h-5 text-teal-500 transform transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`} 
                                    fill="none" 
                                    viewBox="0 0 24 24" 
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            <div 
                                className={`px-6 overflow-hidden transition-all duration-300 ${
                                    openIndex === index ? 'max-h-96 pb-6' : 'max-h-0'
                                }`}
                            >
                                <p className="bg-gradient-to-r from-teal-500 to-green-500 bg-clip-text text-transparent">{faq.answer}</p>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-16 text-center">
                    <div className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gradient-to-r from-green-400 to-teal-400 shadow-sm hover:from-green-500 hover:to-teal-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500">
                        <a href="/company/contact" className="flex items-center">
                            Have more questions? Contact us
                            <svg className="ml-2 -mr-1 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                        </a>
                    </div>
                </div>

                <div className="mt-16 border-t border-green-400 pt-8 text-center">
                    <p className="text-base text-gray-500">
                    </p>
                </div>
            </div>
        </div>
    );
};

export default FAQs;